import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout.jsx";
import { products as prodApi, getAccount, embedProducts } from "../lib/supabase.js";
import ProductEditor from "../components/ProductEditor.jsx";
import ProductCSVImporter from "../components/ProductCSVImporter.jsx";
import { useUserRole, hasPermission } from "../App.jsx";

// ─── Font loader ─────────────────────────────────────────────────────────────

function useFonts() {
  useEffect(() => {
    [
      { id: "gfont-instrument-serif", href: "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap" },
      { id: "gfont-dm-sans", href: "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&display=swap" },
    ].forEach((f) => {
      if (!document.getElementById(f.id)) {
        const link = document.createElement("link");
        link.id = f.id; link.rel = "stylesheet"; link.href = f.href;
        document.head.appendChild(link);
      }
    });
  }, []);
}

// ─── Theme ───────────────────────────────────────────────────────────────────

const L = {
  bg: "#fafaf8", text: "#1a1a1a", textSoft: "#444444", muted: "#8a8a8a",
  dim: "#c0bdb8", border: "#e5e3de", borderLight: "#f0eee9",
  surface: "#ffffff", surfaceHover: "#f6f5f2", inputBg: "#f6f5f2",
};
const sans = "'DM Sans', -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";

const inputStyle = {
  padding: "9px 14px", background: L.inputBg, border: `1px solid ${L.border}`,
  borderRadius: 8, color: L.text, fontSize: 13, outline: "none",
  fontFamily: sans, boxSizing: "border-box",
};
const btnGhost = {
  padding: "8px 16px", background: "none", border: `1px solid ${L.border}`,
  borderRadius: 8, color: L.muted, fontSize: 12, fontWeight: 500,
  cursor: "pointer", fontFamily: sans, whiteSpace: "nowrap",
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function Products() {
  useFonts();
  const account = getAccount();
  const { role } = useUserRole();
  const canEdit = hasPermission(role, "editContent");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [showCSV, setShowCSV] = useState(false);
  const [viewMode, setViewMode] = useState("table");
  const [embedStatus, setEmbedStatus] = useState(null);

  const handleEmbed = async () => {
    setEmbedStatus("running");
    const result = await embedProducts({ limit: 200 });
    setEmbedStatus(result);
    setTimeout(() => setEmbedStatus(null), 5000);
  };

  const loadProducts = useCallback(async () => {
    setLoading(true);
    const { data } = await prodApi.list({ category: catFilter || undefined, search: search || undefined });
    setItems(data || []);
    setSelected(new Set());
    setLoading(false);
  }, [catFilter, search]);

  const loadCategories = async () => {
    const { data } = await prodApi.categories();
    setCategories([...new Set((data || []).map((d) => d.category).filter(Boolean))]);
  };

  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { loadCategories(); }, []);

  const refresh = async () => { await loadProducts(); loadCategories(); };
  const handleDelete = async (id) => {
    if (!confirm("Delete this product?")) return;
    setItems((prev) => prev.filter((p) => p.id !== id));
    setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
    await prodApi.delete(id);
    loadCategories();
  };
  const handleBulkDelete = async () => {
    if (!confirm(`Delete ${selected.size} products?`)) return;
    const ids = [...selected];
    const idSet = new Set(ids);
    setItems((prev) => prev.filter((p) => !idSet.has(p.id)));
    setSelected(new Set());
    for (let i = 0; i < ids.length; i += 100) await prodApi.deleteMany(ids.slice(i, i + 100));
    await loadProducts();
    loadCategories();
  };
  const handleToggleActive = async (item) => { await prodApi.update(item.id, { is_active: !item.is_active }); refresh(); };
  const toggleSelect = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => { selected.size === items.length ? setSelected(new Set()) : setSelected(new Set(items.map((p) => p.id))); };

  const stats = [
    { label: "Total", value: items.length },
    { label: "Active", value: items.filter((p) => p.is_active).length },
    { label: "Categories", value: [...new Set(items.map((p) => p.category).filter(Boolean))].length },
    { label: "With images", value: items.filter((p) => p.image_url).length },
    { label: "Embedded", value: items.filter((p) => p.embedding).length },
  ];

  const embeddedCount = items.filter((p) => p.embedding).length;
  const unembeddedCount = items.filter((p) => !p.embedding && p.is_active).length;

  return (
    <Layout>
      <style>{`@keyframes ov-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>

      <div style={{
        minHeight: "calc(100vh - 56px)", background: L.bg,
        margin: -28, padding: "40px 44px", fontFamily: sans, color: L.text,
      }}>
        {/* ── Header ── */}
        <div style={{ marginBottom: 28, animation: "ov-up 0.5s ease both" }}>
          <h1 style={{ fontSize: 36, fontWeight: 400, lineHeight: 1.1, margin: "0 0 8px", fontFamily: serif, fontStyle: "italic" }}>Products</h1>
          <p style={{ margin: 0, fontSize: 13, color: L.muted }}>Manage your product catalog for the chat widget</p>
        </div>

        {/* ── Brief ── */}
        {items.length > 0 && (
          <div style={{ padding: "20px 0 18px", borderBottom: `1px solid ${L.border}`, marginBottom: 22, animation: "ov-up 0.5s ease 0.03s both" }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: L.muted, marginBottom: 12 }}>The brief</div>
            <div style={{ fontSize: 14, lineHeight: 1.75, color: L.textSoft, fontFamily: sans, maxWidth: 680 }}>
              {embeddedCount} of {items.length} product{items.length !== 1 ? "s are" : " is"} embedded and searchable by your chat widget.
              {unembeddedCount > 0 ? ` ${unembeddedCount} product${unembeddedCount !== 1 ? "s were" : " was"} added since the last embedding — run Embed to make ${unembeddedCount !== 1 ? "them" : "it"} available to the AI.` : " All active products are available to the AI."}
            </div>
          </div>
        )}

        {/* ── Toolbar ── */}
        <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", animation: "ov-up 0.5s ease 0.05s both" }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products..." style={{ ...inputStyle, flex: 1, minWidth: 200, width: "auto" }} />
          <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} style={{ ...inputStyle, width: "auto", cursor: "pointer" }}>
            <option value="">All categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {canEdit && (
            <button onClick={() => setEditing("new")} style={{
              padding: "9px 20px", background: L.text, border: "none", borderRadius: 8,
              color: L.bg, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: sans,
            }}>
              + Add product
            </button>
          )}
          {canEdit && <button onClick={() => setShowCSV(true)} style={btnGhost}>Import CSV</button>}
          {canEdit && (
            <button onClick={handleEmbed} disabled={embedStatus === "running"} style={{
              ...btnGhost, color: embedStatus === "running" ? L.dim : "#6d28d9",
              borderColor: embedStatus === "running" ? L.border : "#ddd6fe",
              cursor: embedStatus === "running" ? "not-allowed" : "pointer",
            }}>
              {embedStatus === "running" ? "Embedding..." : "Embed"}
            </button>
          )}
          {embedStatus && embedStatus !== "running" && (
            <span style={{ fontSize: 11, color: embedStatus.error ? "#dc2626" : "#15803d", alignSelf: "center" }}>
              {embedStatus.error || embedStatus.message}
            </span>
          )}
          {/* View toggle */}
          <div style={{ display: "flex", gap: 1, background: L.inputBg, borderRadius: 8, border: `1px solid ${L.border}`, padding: 2 }}>
            {[
              { key: "table", label: "List" },
              { key: "grid", label: "Grid" },
            ].map((v) => (
              <button key={v.key} onClick={() => setViewMode(v.key)} style={{
                padding: "6px 12px", background: viewMode === v.key ? L.surface : "transparent",
                border: "none", borderRadius: 6, color: viewMode === v.key ? L.text : L.muted,
                fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: sans,
                boxShadow: viewMode === v.key ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
              }}>
                {v.label}
              </button>
            ))}
          </div>
          {canEdit && selected.size > 0 && (
            <button onClick={handleBulkDelete} style={{ ...btnGhost, color: "#dc2626", borderColor: "#fecaca" }}>
              Delete {selected.size}
            </button>
          )}
        </div>

        {/* ── Stats ── */}
        <div style={{
          display: "flex", gap: 0, marginBottom: 18,
          borderTop: `1px solid ${L.border}`, borderBottom: `1px solid ${L.border}`,
          animation: "ov-up 0.5s ease 0.08s both",
        }}>
          {stats.map((s, i) => (
            <div key={s.label} style={{
              padding: "12px 16px", flex: 1,
              borderRight: i < stats.length - 1 ? `1px solid ${L.border}` : "none",
            }}>
              <div style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", color: L.muted, marginBottom: 3 }}>{s.label}</div>
              <div style={{ fontSize: 20, fontWeight: 300, letterSpacing: "-0.02em" }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* ── Empty state ── */}
        {!loading && items.length === 0 && !search && !catFilter && (
          <div style={{
            background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12,
            padding: "64px 20px", textAlign: "center", animation: "ov-up 0.5s ease 0.1s both",
          }}>
            <div style={{ fontSize: 14, color: L.muted, marginBottom: 20 }}>
              No products yet.{canEdit ? " Add your first product or import from CSV." : ""}
            </div>
            {canEdit && (
              <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                <button onClick={() => setEditing("new")} style={{
                  padding: "10px 24px", background: L.text, border: "none", borderRadius: 8,
                  color: L.bg, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: sans,
                }}>Add product</button>
                <button onClick={() => setShowCSV(true)} style={btnGhost}>Import CSV</button>
              </div>
            )}
          </div>
        )}

        {/* ── Grid view ── */}
        {viewMode === "grid" && items.length > 0 && (
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 16, animation: "ov-up 0.5s ease 0.1s both",
          }}>
            {items.map((p) => (
              <div
                key={p.id}
                onClick={() => canEdit && setEditing(p)}
                style={{
                  background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12,
                  overflow: "hidden", cursor: canEdit ? "pointer" : "default",
                  opacity: p.is_active ? 1 : 0.5, transition: "border-color 0.15s, box-shadow 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = L.dim; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.04)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = L.border; e.currentTarget.style.boxShadow = "none"; }}
              >
                {p.image_url ? (
                  <div style={{ height: 160, background: L.inputBg, overflow: "hidden" }}>
                    <img src={p.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.target.style.display = "none"; }} />
                  </div>
                ) : (
                  <div style={{ height: 72, background: L.inputBg, display: "flex", alignItems: "center", justifyContent: "center", color: L.dim, fontSize: 13 }}>No image</div>
                )}
                <div style={{ padding: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{p.name}</div>
                  {p.sku && <div style={{ fontSize: 11, color: L.muted, fontFamily: "monospace", marginBottom: 6 }}>SKU: {p.sku}</div>}
                  {p.category && (
                    <span style={{
                      display: "inline-block", padding: "2px 8px", borderRadius: 20,
                      fontSize: 10, fontWeight: 600, textTransform: "uppercase",
                      background: L.inputBg, color: L.muted, border: `1px solid ${L.borderLight}`,
                      letterSpacing: "0.04em",
                    }}>{p.category}</span>
                  )}
                  {p.short_description && (
                    <div style={{
                      fontSize: 12, color: L.muted, marginTop: 8, lineHeight: 1.5,
                      display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                    }}>{p.short_description}</div>
                  )}
                  <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
                    {p.price && <span style={{ fontSize: 13, fontWeight: 600, marginLeft: "auto" }}>{p.price_label || "$"}{p.price}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Table view ── */}
        {viewMode === "table" && items.length > 0 && (
          <div style={{
            background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12,
            overflow: "hidden", animation: "ov-up 0.5s ease 0.1s both",
          }}>
            {/* Header */}
            <div style={{
              display: "flex", padding: "12px 20px", borderBottom: `1px solid ${L.border}`,
              fontSize: 10, color: L.muted, fontWeight: 600, textTransform: "uppercase",
              letterSpacing: "0.1em", gap: 12, alignItems: "center",
            }}>
              <div style={{ width: 24 }}>{canEdit && <input type="checkbox" checked={selected.size === items.length && items.length > 0} onChange={toggleSelectAll} style={{ cursor: "pointer" }} />}</div>
              <div style={{ width: 40 }} />
              <div style={{ flex: 2 }}>Product</div>
              <div style={{ width: 100 }}>SKU</div>
              <div style={{ width: 120 }}>Category</div>
              <div style={{ width: 60, textAlign: "center" }}>Active</div>
              <div style={{ width: 80, textAlign: "right" }}>Price</div>
              <div style={{ width: 40 }} />
            </div>

            {/* Rows */}
            {loading ? (
              <div style={{ padding: "40px 20px", color: L.muted, fontSize: 13, textAlign: "center" }}>Loading...</div>
            ) : items.length === 0 ? (
              <div style={{ padding: "48px 20px", color: L.muted, fontSize: 13, textAlign: "center" }}>No matches.</div>
            ) : items.map((p) => (
              <div
                key={p.id}
                style={{
                  display: "flex", padding: "12px 20px", borderBottom: `1px solid ${L.borderLight}`,
                  alignItems: "center", gap: 12, opacity: p.is_active ? 1 : 0.45,
                  cursor: "pointer", transition: "background 0.1s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = L.surfaceHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = L.surface; }}
              >
                <div style={{ width: 24 }}>{canEdit && <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSelect(p.id)} onClick={(e) => e.stopPropagation()} style={{ cursor: "pointer" }} />}</div>
                <div style={{ width: 40, height: 40, borderRadius: 8, overflow: "hidden", background: L.inputBg, flexShrink: 0 }}>
                  {p.image_url ? (
                    <img src={p.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.target.style.display = "none"; }} />
                  ) : (
                    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: L.dim, fontSize: 11 }}>—</div>
                  )}
                </div>
                <div style={{ flex: 2 }} onClick={() => canEdit && setEditing(p)}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{p.name}</div>
                  {p.short_description && (
                    <div style={{ fontSize: 11, color: L.muted, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 300 }}>
                      {p.short_description}
                    </div>
                  )}
                </div>
                <div style={{ width: 100, fontSize: 12, color: L.muted, fontFamily: "monospace" }}>{p.sku || "—"}</div>
                <div style={{ width: 120 }}>
                  {p.category ? (
                    <span style={{
                      padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 600,
                      textTransform: "uppercase", background: L.inputBg, color: L.muted,
                      border: `1px solid ${L.borderLight}`, letterSpacing: "0.04em",
                    }}>{p.category}</span>
                  ) : <span style={{ color: L.dim }}>—</span>}
                </div>
                <div style={{ width: 60, textAlign: "center" }}>
                  {canEdit ? (
                    <button onClick={(e) => { e.stopPropagation(); handleToggleActive(p); }} style={{
                      width: 20, height: 20, borderRadius: 4, border: `1px solid ${p.is_active ? "#16a34a" : L.border}`,
                      background: p.is_active ? "#16a34a" : "transparent", cursor: "pointer",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      color: "#fff", fontSize: 11, transition: "all 0.15s",
                    }}>
                      {p.is_active ? "✓" : ""}
                    </button>
                  ) : (
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: p.is_active ? "#16a34a" : L.dim, display: "inline-block" }} />
                  )}
                </div>
                <div style={{ width: 80, textAlign: "right", fontSize: 13, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
                  {p.price ? `${p.price_label || "$"}${p.price}` : "—"}
                </div>
                <div style={{ width: 40, textAlign: "center" }}>
                  {canEdit && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }}
                      style={{ background: "none", border: "none", color: L.dim, cursor: "pointer", fontSize: 13, transition: "color 0.15s" }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#dc2626"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = L.dim; }}
                    >×</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modals */}
        {canEdit && editing && <ProductEditor product={editing === "new" ? null : editing} color={account?.widget_color || "#6366f1"} onSave={refresh} onClose={() => setEditing(null)} />}
        {canEdit && showCSV && <ProductCSVImporter color={account?.widget_color || "#6366f1"} onComplete={refresh} onClose={() => setShowCSV(false)} />}
      </div>
    </Layout>
  );
}
